import { useState, type ReactNode } from "react";
import { z } from "zod";
import {
  Box,
  Icon,
  Text,
  WidgetEmpty,
  WidgetListRow,
  WidgetSkeleton,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { EntityTag } from "#app/components/entity";
import { useIssueView } from "#app/components/issue-view";
import { ProjectRowAge } from "#app/components/projects/project-list-row";
import type { IssueRow } from "#app/lib/data";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { useAddedList } from "./use-added-list";

/**
 * `added-list` — the viewer's newest Projects, newest first. A row leads with
 * the project glyph and then the name, and says only the follow-up age and
 * when the row was added — NO stage glyph and NO assignees (Rob, 2026-09-05:
 * "we may not need to see the stage and assignee unless the user clicks on
 * it. age is fine to see"); the press opens the detail, where those live. The
 * age chip stays live: its press opens the Update dialog, as it does on every
 * project row.
 *
 * A **managed-only** card (the My Work catalog) — it contributes no `catalog`
 * entry, so it never appears in the free Add-card gallery.
 */
export const addedListConfigSchema = z.looseObject({
  limit: z.number().int().min(3).max(20).default(10),
});

export type AddedListConfig = z.infer<typeof addedListConfigSchema>;

function AddedListRender({ config }: WidgetRenderContext<AddedListConfig>) {
  const { items, issuesLoading } = useAddedList(config.limit);
  const { openIssue } = useIssueView();
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const emptyTitle = useWindowEmptyTitle("Nothing added");

  /** One row: the project glyph and the name on the left, the clock and the
   *  added-when on the right. */
  const renderItem = (issue: IssueRow): ReactNode => (
    <WidgetListRow key={issue.issueId} onClick={() => openIssue(issue, { source: "widget" })}>
      <Box align="center" direction="row" gap="sm" minWidth={0} shrink>
        <Icon color="muted" name={ENTITY_KIND_ICON.issue} size="sm" />
        <EntityTag
          display="label"
          entity={{
            kind: "issue",
            id: issue.issueId,
            issue: { name: issue.name },
            workflowId: issue.workflowId,
          }}
          interactive={false}
          weight="regular"
        />
      </Box>
      <Box align="center" direction="row" gap="sm" justify="end" shrink={false}>
        <ProjectRowAge issue={issue} onAgeClick={setAgeDialogIssue} />
        <Text size="xs" variant="muted" tabular>
          {formatRelativeDate(issue.creationDate, "compact")}
        </Text>
      </Box>
    </WidgetListRow>
  );

  const body: ReactNode =
    issuesLoading && items.length === 0 ? (
      <WidgetSkeleton />
    ) : items.length === 0 ? (
      <WidgetEmpty title={emptyTitle} />
    ) : (
      items.map(renderItem)
    );

  return (
    <>
      <Box direction="column">{body}</Box>
      {ageDialogIssue ? (
        <UpdateAgeDialog
          via="added_list"
          age={ageDialogIssue.age}
          issueId={ageDialogIssue.issueId}
          onOpenChange={(next) => {
            if (!next) setAgeDialogIssue(null);
          }}
          open
          stageId={ageDialogIssue.stageId}
        />
      ) : null}
    </>
  );
}

const addedListSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      { key: "limit", kind: "number", label: "Limit", icon: "list", min: 3, max: 20, step: 1 },
    ],
  },
];

export const addedListDef: WidgetModule<AddedListConfig> = {
  type: "added-list",
  title: "Recently added",
  icon: "sparkles",
  // The rows are the head of the scoped project set the card already orders
  // by creation, so the Data tab hides — the press on a row is the drilldown.
  drilldownDataset: null,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 6 },
  configSchema: addedListConfigSchema,
  settings: addedListSettings,
  render: AddedListRender,
};
